import { FC, useEffect } from "react";

import { t } from "i18n/i18n";
import { Listbox } from "mds/components/Listbox";
import { storeApi } from "store/index";
import { selectSectionMenuOptions } from "store/selectors";
import { useListSelector } from "store/store-hooks";

export type SectionMenuProps = {
  selectedSectionId: string | null;
  onSectionChange: (sectionId: string) => void;
  size?: "xs" | "s";
  showEmptyOption?: boolean;
  /** Text for the empty option when showEmptyOption is true. Defaults to "Select section". */
  emptyOptionText?: string;
  /** Limit options to this term. */
  termId?: string | null;
  /** Limit options to this course. */
  courseId?: string | null;
  /** When true, only template sections are listed. */
  templatesOnly?: boolean;
  disabled?: boolean;
  id?: string;
  "aria-invalid"?: boolean;
  "aria-describedby"?: string;
  "aria-required"?: boolean;
};

export const SectionMenu: FC<SectionMenuProps> = ({
  selectedSectionId,
  onSectionChange,
  size = "xs",
  showEmptyOption = true,
  emptyOptionText = t("import_modal.select_section"),
  termId = null,
  courseId = null,
  templatesOnly = false,
  disabled = false,
  id,
  "aria-invalid": ariaInvalid,
  "aria-describedby": ariaDescribedBy,
  "aria-required": ariaRequired,
}) => {
  const sectionOptions = useListSelector(
    (state) => selectSectionMenuOptions(state, termId, courseId, templatesOnly),
    ["id", "title"],
  );
  const emptyOption = { id: "", title: emptyOptionText };
  const listboxItems = showEmptyOption ? [emptyOption, ...sectionOptions] : sectionOptions;

  useEffect(() => {
    storeApi.sections.list();
  }, []);

  return (
    <Listbox
      aria-describedby={ariaDescribedBy}
      aria-invalid={ariaInvalid}
      aria-required={ariaRequired}
      buttonSize={size}
      className="w-full"
      disabled={disabled}
      id={id}
      items={listboxItems}
      selectedValue={selectedSectionId ?? ""}
      onSelectionChange={(item) => onSectionChange(item.id)}
    />
  );
};
